import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

import AmplifyApiService from '../../services/AmplifyApiService';
import IAmplifyApiConfig from '../../shared/interfaces/IAmplifyApiConfig';
import { CreateSiteProps, ISite, UpdateSiteProps } from '../../shared/interfaces/ISite';

const amplifyApiService = new AmplifyApiService();

const SITES_BASE_PATH = 'sites';

export const useGetSites = (
  options: UseQueryOptions<ISite[], Error> = {},
): UseQueryResult<ISite[], Error> => {
  const amplifyApiConfig: IAmplifyApiConfig = {
    apiName: 'PacificApi',
    path: SITES_BASE_PATH,
  };

  const query = useQuery<ISite[], Error>({
    queryKey: [SITES_BASE_PATH],
    queryFn: async (): Promise<ISite[]> => {
      return await amplifyApiService.get<ISite[]>(amplifyApiConfig);
    },
    ...options,
  });

  return query;
};

export const useCreateSite = (
  options: UseMutationOptions<ISite, Error, CreateSiteProps> = {},
): UseMutationResult<ISite, Error, CreateSiteProps> => {
  const mutation = useMutation<ISite, Error, CreateSiteProps>({
    mutationFn: async (props: CreateSiteProps): Promise<ISite> => {
      const amplifyApiConfig: IAmplifyApiConfig = {
        apiName: 'PacificApi',
        path: SITES_BASE_PATH,
        options: {
          body: props,
        },
      };

      return await amplifyApiService.post<CreateSiteProps, ISite>(amplifyApiConfig);
    },
    ...options,
  });

  return mutation;
};

export const useUpdateSite = (
  options: UseMutationOptions<void, Error, UpdateSiteProps> = {},
): UseMutationResult<void, Error, UpdateSiteProps> => {
  const mutation = useMutation<void, Error, UpdateSiteProps>({
    mutationFn: async ({ id, name }: UpdateSiteProps): Promise<void> => {
      const amplifyApiConfig: IAmplifyApiConfig = {
        apiName: 'PacificApi',
        path: `${SITES_BASE_PATH}/${id}`,
        options: {
          body: { name },
        },
      };

      await amplifyApiService.put<{ name: string }, void>(amplifyApiConfig);
    },
    ...options,
  });

  return mutation;
};
